import { useEffect, useState } from "react";
import { ExternalLink, Clock, MapPin, ArrowRight, RefreshCw } from "lucide-react";
import { upcoming as fallback, coursesUrl, riskPaket } from "../mock/mock";

const API = process.env.REACT_APP_BACKEND_URL;
const ARIAL = { fontFamily: "Arial, Helvetica, sans-serif" };

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / 86400000);
  if (diff <= 0) return "Idag";
  if (diff === 1) return "Imorgon";
  return `Om ${diff} dagar`;
};

const isHalkbana = (t) => /halkbana|del 2/i.test(t);
const kind = (t) => (isHalkbana(t) ? "Halkbana" : /del 1|riskettan/i.test(t) ? "Riskettan" : "Kurs");

const legend = [
  { title: "Riskettan", text: "3 timmar exkl. raster · alkohol, droger & trötthet" },
  { title: "Halkbana", text: "3 timmar exkl. raster · vi skjutsar dig tur och retur (6 timmar totalt)" },
];

export default function UpcomingCourses() {
  const [sessions, setSessions] = useState(fallback);
  const [paket, setPaket] = useState(riskPaket);
  const [updated, setUpdated] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/courses`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (d.sessions?.length) setSessions(d.sessions);
        if (d.updated_at) setUpdated(new Date(d.updated_at));
        const p = d.products?.find((x) => /paket/i.test(x.name));
        if (p) setPaket({ ...riskPaket, title: p.name, price: `${p.price.toLocaleString("sv-SE")} kr`, url: p.url });
      })
      .catch(() => {});
  }, []);

  const saving = paket.ord && paket.price ? parseInt(paket.ord.replace(/\D/g, ""), 10) - parseInt(paket.price.replace(/\D/g, ""), 10) : null;
  const soonest = sessions[0]?.start ? daysUntil(sessions[0].start) : null;

  return (
    <section className="max-w-7xl mx-auto px-5 -mt-16 relative z-10" data-testid="upcoming-section" style={ARIAL}>
      <div className="grid lg:grid-cols-12 rounded-3xl overflow-hidden shadow-2xl shadow-[#0B2545]/25 ring-1 ring-black/5">
        {/* Left: brand panel */}
        <aside className="lg:col-span-4 relative overflow-hidden bg-[#ffce3a] p-6 md:p-8 flex flex-col justify-between">
          <div className="absolute inset-0 upcoming-stripes pointer-events-none" />
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-white/25 blur-2xl pointer-events-none" />
          <div className="absolute -left-10 bottom-10 w-40 h-40 rounded-full bg-[#0B2545]/10 blur-2xl pointer-events-none" />

          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B2545] text-[#ffce3a] font-bold tracking-[0.2em] text-[11px] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ffce3a] animate-pulse" /> På gång
            </span>
            <h2 className="text-3xl md:text-[2.3rem] font-bold text-[#0B2545] mt-4 leading-[1.02] tracking-tight">
              Kommande<br />kurstillfällen
            </h2>
            <p className="text-[#0B2545]/80 mt-3 text-sm leading-relaxed max-w-xs">
              Riskettan och halkbana är obligatoriska inför proven. Välj datum och säkra din plats direkt – det tar en minut.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3" data-testid="upcoming-stats">
              <div className="rounded-2xl bg-white/70 backdrop-blur px-4 py-3.5 border border-white/60">
                <div className="text-3xl font-bold text-[#0B2545] leading-none">{sessions.length}</div>
                <div className="text-[11px] font-semibold text-[#0B2545]/70 mt-1.5 uppercase tracking-wider">Tillfällen</div>
              </div>
              <div className="rounded-2xl bg-[#0B2545] px-4 py-3.5 text-white">
                <div className="text-lg font-bold leading-none mt-1 text-[#ffce3a]">{soonest || "Snart"}</div>
                <div className="text-[11px] font-semibold text-slate-300 mt-2 uppercase tracking-wider">Nästa kurs</div>
              </div>
            </div>

            <ul className="mt-5 space-y-2">
              {legend.map(({ title, text }) => (
                <li key={title} className="pl-3 border-l-2 border-[#0B2545]/30 text-[#0B2545] leading-tight">
                  <span className="block text-sm font-bold">{title}</span><span className="block text-xs text-[#0B2545]/70 mt-0.5">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative mt-6 flex flex-wrap items-center justify-between gap-3">
            <a href={coursesUrl} target="_blank" rel="noopener noreferrer" data-testid="all-courses-link" className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0B2545] text-white text-sm font-bold hover:bg-[#13315C] transition-colors shadow-lg shadow-[#0B2545]/25">
              Se alla kurser <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0B2545]/60" data-testid="upcoming-updated">
              <RefreshCw size={11} /> {updated ? `Uppdaterad ${updated.toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}` : "Uppdateras automatiskt"}
            </span>
          </div>
        </aside>

        {/* Right: schedule list */}
        <div className="lg:col-span-8 bg-white flex flex-col">
          <ul className="divide-y divide-slate-100 flex-1">
            {sessions.map((u, i) => {
              const halk = isHalkbana(u.title);
              const [datePart, timePart, place] = (u.detail || "").split(" · ");
              const full = u.spots === "Fullbokat";
              const few = typeof u.remaining === "number" && u.remaining <= 4;
              const when = u.start ? daysUntil(u.start) : null;
              const urgent = when === "Idag" || when === "Imorgon";
              return (
                <li key={`${u.url}-${u.start || i}`} className="upcoming-row" style={{ animationDelay: `${i * 80}ms` }}>
                  <a href={full ? undefined : u.url} onClick={(e) => full && e.preventDefault()} target={full ? undefined : "_blank"} rel="noopener noreferrer" data-testid={`upcoming-course-${i}`} className={`group relative grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2.5 items-center px-5 md:px-6 py-3.5 md:py-4 transition-colors ${full ? "bg-rose-50/60 cursor-not-allowed" : "hover:bg-[#fffbea]"}`} aria-disabled={full} data-full={full ? "true" : "false"}>
                    <span className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-[#ffce3a] scale-y-0 group-hover:scale-y-100 origin-center transition-transform" />
                    <div className={`w-[60px] rounded-xl py-2 text-center flex-shrink-0 transition-transform group-hover:-translate-y-0.5 ${full ? "bg-[#c8102e] text-white" : halk ? "bg-[#0B2545] text-white" : "bg-[#f0f5fb] text-[#0B2545]"}`}>
                      <div className={`text-[10px] font-bold tracking-[0.2em] uppercase ${full ? "text-white/80" : halk ? "text-[#ffce3a]" : "text-[#1B6CA8]"}`}>{u.date.month}</div>
                      <div className="text-[1.7rem] font-bold leading-none mt-0.5">{u.date.day}</div>
                      <div className={`text-[11px] mt-1 ${full ? "text-white/80" : halk ? "text-slate-300" : "text-slate-500"}`}>{datePart?.split(" ")[0]}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide ${halk ? "bg-[#0B2545] text-[#ffce3a]" : "bg-[#ffce3a] text-[#0B2545]"}`}>
                          {kind(u.title)}
                        </span>
                        {when && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${urgent ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
                            {when}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-[#0B2545] mt-1 leading-tight">{u.title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[13px] text-slate-500">
                        <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-[#1B6CA8]" /> {timePart} <span className="text-xs text-slate-400">{halk ? "(inkl. skjuts t/r)" : "(exkl. raster)"}</span></span>
                        <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#1B6CA8]" /> {place || "Torkelsgatan 29"}</span>
                      </div>
                    </div>
                    <div className="col-span-2 sm:col-span-1 flex sm:flex-col items-center sm:items-end justify-between gap-2 sm:gap-2.5 sm:text-right">
                      <span data-testid="course-spots" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold whitespace-nowrap uppercase tracking-wide ${full ? "bg-[#c8102e] text-white" : few ? "bg-amber-50 text-amber-700 normal-case" : "bg-emerald-50 text-emerald-700 normal-case"}`}>
                        {!full && <span className={`w-1.5 h-1.5 rounded-full ${few ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />} {full ? "Fullbokat" : u.spots}
                      </span>
                      <span className="flex items-center gap-3 sm:contents">
                      <span className={`text-lg sm:text-xl font-bold leading-none whitespace-nowrap ${full ? "text-slate-400 line-through" : "text-[#0B2545]"}`}>{u.price}</span>
                      {!full && (
                        <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-bold whitespace-nowrap transition-all bg-[#f0f5fb] text-[#0B2545] group-hover:bg-[#0B2545] group-hover:text-white group-hover:shadow-lg group-hover:shadow-[#0B2545]/25">
                          Boka plats <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      )}
                      </span>
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>

          <a href={paket.url} target="_blank" rel="noopener noreferrer" data-testid="risk-paket-link" className="group relative overflow-hidden flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-5 md:px-6 py-4 bg-[#0B2545] text-white hover:bg-[#0e2b52] transition-colors">
            <div className="absolute -right-10 -top-16 w-56 h-56 rounded-full bg-[#ffce3a]/10 blur-3xl pointer-events-none" />
            <span className="relative flex items-center gap-4 min-w-0">
              <span className="min-w-0">
                <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ffce3a]">Paketpris {saving ? <span className="px-2 py-0.5 rounded-md bg-emerald-400/20 text-emerald-300 tracking-normal normal-case">Spara {saving} kr</span> : null}</span>
                <span className="block text-base md:text-lg font-bold mt-1 leading-tight">{paket.title} – boka riskettan och halkbanan tillsammans</span>
              </span>
            </span>
            <span className="relative flex items-center gap-3 flex-shrink-0 sm:pl-6 sm:border-l sm:border-white/10">
              <span className="flex flex-col items-end leading-none">
                <span className="text-2xl md:text-3xl font-bold text-[#ffce3a]">{paket.price}</span>
                <span className="text-xs text-slate-400 line-through mt-1">Ord. {paket.ord}</span>
              </span>
              <span className="w-11 h-11 rounded-full bg-[#ffce3a] text-[#0B2545] flex items-center justify-center group-hover:translate-x-1 transition-transform"><ArrowRight size={20} /></span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
